import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import DayExerciseManager from './DayExerciseManager';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Calendar as CalendarIcon, 
  Dumbbell, 
  CheckCircle,
  Clock,
  Target,
  Activity,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  TrendingUp,
  Users,
  X,
  Save,
  Eye
} from 'lucide-react';

interface User {
  id: string;
  full_name: string;
  email: string;
}

interface Exercise {
  id: string;
  name: string;
  description: string;
  created_by: string;
  created_at: string;
}

interface UserExercise {
  id: string;
  user_id: string;
  exercise_id: string;
  scheduled_date: string;
  sets: number;
  reps: number;
  weight?: string;
  exercises: Exercise;
}

interface BeautifulExerciseCalendarProps {
  selectedUser: string;
  users: User[];
  exercises: Exercise[];
  onExerciseUpdated: () => void;
}

const BeautifulExerciseCalendar: React.FC<BeautifulExerciseCalendarProps> = ({
  selectedUser,
  users,
  exercises,
  onExerciseUpdated
}) => {
  const [userExercises, setUserExercises] = useState<UserExercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDayManager, setShowDayManager] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());


  const selectedUserData = users.find(u => u.id === selectedUser);

  // Load user exercises
  useEffect(() => {
    if (selectedUser) {
      loadUserExercises();
    }
  }, [selectedUser]);

  const loadUserExercises = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Loading exercises for user:', selectedUser);

      const { data, error } = await supabase
        .from('user_exercises')
        .select(`
          *,
          exercises (*)
        `)
        .eq('user_id', selectedUser)
        .order('scheduled_date', { ascending: true });

      if (error) {
        console.error('❌ Supabase error:', error);
        throw error;
      }

      console.log('✅ Exercises loaded:', data?.length || 0, 'exercises');
      console.log('📋 Exercise details:', data);

      setUserExercises(data || []);
    } catch (err) {
      console.error('❌ Error loading exercises:', err);
      setError('Failed to load exercises');
    } finally {
      setLoading(false);
    }
  };



  // Handle date selection
  const handleDateSelect = (date: Date) => {
    // Convert date to YYYY-MM-DD format using local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    setSelectedDate(dateString);
    setShowDayManager(true);
    setError(null); // Clear any previous errors
    setSuccess(null); // Clear any previous success messages
  };

  // Get exercises for a specific date
  const getExercisesForDate = (date: Date) => {
    // Convert date to YYYY-MM-DD format using local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return userExercises.filter(ex => ex.scheduled_date === dateStr);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardContent className="py-12">
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="text-muted-foreground">Loading calendar...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <Alert variant="destructive">
          <Clock className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Exercise Summary - Apple Style */}
      {userExercises.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-background via-background to-muted/20 rounded-xl p-4 border border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <Dumbbell className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-lg font-semibold text-foreground">{userExercises.length}</p>
                <p className="text-xs text-muted-foreground">Total Exercises</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-background via-background to-muted/20 rounded-xl p-4 border border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-lg font-semibold text-foreground">{userExercises.length}</p>
                <p className="text-xs text-muted-foreground">Total Scheduled</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-background via-background to-muted/20 rounded-xl p-4 border border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <Clock className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-lg font-semibold text-foreground">
                  {new Set(userExercises.map(ex => ex.scheduled_date)).size}
                </p>
                <p className="text-xs text-muted-foreground">Active Days</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-background via-background to-muted/20 rounded-xl p-4 border border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <Target className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-lg font-semibold text-foreground">
                  {new Set(userExercises.map(ex => ex.exercise_id)).size}
                </p>
                <p className="text-xs text-muted-foreground">Unique Exercises</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Beautiful Calendar */}
      <Card className="shadow-lg border border-gray-700 bg-gray-900">

        <CardContent className="p-3 sm:p-4 md:p-6">
          {/* Calendar Navigation */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
            <div className="flex items-center justify-center sm:justify-start space-x-2 sm:space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                className="h-8 w-8 sm:h-9 sm:w-auto px-2 sm:px-3"
              >
                <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
              <h2 className="text-lg sm:text-xl font-bold text-white text-center sm:text-left">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                className="h-8 w-8 sm:h-9 sm:w-auto px-2 sm:px-3"
              >
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(new Date())}
              className="h-8 px-3 sm:h-9 sm:px-4 text-xs sm:text-sm"
            >
              Today
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="p-1 sm:p-2 md:p-3 text-center text-xs sm:text-sm font-semibold text-gray-300 bg-gray-800 rounded-lg">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {calendarDays.map((date, index) => {
              const dayExercises = getExercisesForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
              const isPast = date < new Date() && !isToday;
              
              return (
                <div
                  key={index}
                  className={`
                    min-h-[50px] sm:min-h-[80px] md:min-h-[100px] p-1 sm:p-2 border rounded-lg transition-all duration-200
                    ${isCurrentMonth ? 'bg-gray-800 text-white' : 'bg-gray-900 text-gray-500'}
                    ${isToday ? 'bg-blue-900 border-blue-500 ring-1 sm:ring-2 ring-blue-400' : 'border-gray-600'}
                    ${isPast ? 'opacity-60 cursor-default' : 'cursor-pointer hover:shadow-md hover:scale-102 hover:border-blue-500'}
                    ${dayExercises.length > 0 ? 'bg-green-900 border-green-500' : ''}
                    ${!isPast ? 'hover:bg-gray-700' : ''}
                  `}
                  onClick={() => !isPast && handleDateSelect(date)}
                  title={isPast ? 'Past date - click exercises to edit' : 'Click to add exercise for this day'}
                >
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className={`text-xs sm:text-sm font-medium ${isToday ? 'text-blue-300' : 'text-white'}`}>
                      {date.getDate()}
                    </span>
                    {dayExercises.length > 0 ? (
                      <Badge variant="secondary" className="text-xs px-1 py-0.5">
                        {dayExercises.length}
                      </Badge>
                    ) : !isPast ? (
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-200 rounded-full opacity-50"></div>
                    ) : null}
                  </div>
                  
                  {dayExercises.length > 0 && (
                    <div className="space-y-0.5 sm:space-y-1">
                      {/* Mobile: Only show green indicator */}
                      <div className="sm:hidden">
                        <div className="w-full h-1 bg-green-500 rounded-full"></div>
                      </div>
                      
                      {/* Desktop: Show exercise details */}
                      <div className="hidden sm:block">
                        {dayExercises.slice(0, 2).map((exercise) => (
                          <div
                            key={exercise.id}
                            className="p-2 rounded text-xs bg-blue-800 border border-blue-600 text-blue-100"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-1">
                                <Dumbbell className="h-3 w-3" />
                                <span className="font-medium truncate text-xs">{exercise.exercises.name}</span>
                              </div>
                              <Clock className="h-3 w-3 text-blue-300" />
                            </div>
                            <div className="text-xs text-blue-200 mt-1">
                              {exercise.sets}×{exercise.reps}
                              {exercise.weight && ` • ${exercise.weight}kg`}
                            </div>
                          </div>
                        ))}
                        
                        {dayExercises.length > 2 && (
                          <div className="text-xs text-gray-400 text-center">
                            +{dayExercises.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Day Exercise Manager */}
      {showDayManager && selectedDate && (
        <DayExerciseManager
          selectedUser={selectedUser}
          users={users}
          exercises={exercises}
          selectedDate={selectedDate}
          onClose={() => {
            setShowDayManager(false);
            setSelectedDate(null);
          }}
          onExerciseUpdated={() => {
            loadUserExercises();
            onExerciseUpdated();
          }}
        />
      )}
    </div>
  );
};

export default BeautifulExerciseCalendar;